import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { TAGS_PER_CONTRACT_MAX, type ContractTagSuggestion } from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { suggestContractTags } from '../api/contract-tags';
import { loginRequest, msalInstance } from '../auth/msal';
import { previewOf } from './tag-preview';

/**
 * The tag editor — everywhere a contract's tags are set or a tag filter is built
 * (CLM-77, R-77-1).
 *
 * Chips with a remove button, and one text box. The raw text is previewed live
 * through `normaliseTag` — *"will be stored as `finance contracts`"*, or *"not
 * allowed: only a–z, 0–9, space, hyphen"* — so what is typed is never what is
 * stored without the person seeing the difference. Enter, comma or Tab commit
 * the **normalised** tag; a duplicate is folded silently; the 21st is refused.
 *
 * ## Suggestions steer the vocabulary
 *
 * From the first character, after 250 ms, the box asks `contract-tags-suggest`
 * — the tags already on contracts in the caller's **scope**, most-used first —
 * and lists them with counts. In edit mode a *Create "…"* row closes the list
 * when nothing matches exactly; in filter mode (`mode="filter"`) there is no
 * create row, because a filter for a tag no contract carries is an empty page
 * and the person deserves to know before they ask.
 *
 * ## One normaliser
 *
 * `normaliseTag` is imported from `@clm/shared-types` — the same function the
 * API uses to re-normalise and 400. There is no local copy.
 *
 * ## Rule 11
 *
 * No `fetch` here: the one request is `suggestContractTags` in `src/api`.
 */

export interface TagInputProps {
  /** The input's id; the surface's `<label htmlFor>` points at it. */
  readonly id: string;
  /** Accessible name when the surface has no `<label>` (table cells, popovers). */
  readonly label?: string;
  /** Normalised tags. */
  readonly value: readonly string[];
  readonly onChange: (tags: readonly string[]) => void;
  readonly disabled?: boolean;
  /** Defaults to `TAGS_PER_CONTRACT_MAX` (20). */
  readonly max?: number;
  /**
   * `edit` (default) offers *Create "…"* for a tag not yet in scope; `filter`
   * offers only tags that exist, because the filter is all-of over real rows.
   */
  readonly mode?: 'edit' | 'filter';
  /** Injectable for tests and for surfaces that already hold the vocabulary; defaults to the API. */
  readonly suggest?: (q: string, signal: AbortSignal) => Promise<readonly ContractTagSuggestion[]>;
  readonly placeholder?: string;
}

const SUGGEST_DEBOUNCE_MS = 250;
const SUGGEST_LIMIT = 10;

type SuggestStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'done' }
  | { readonly kind: 'error'; readonly detail: string };

async function defaultSuggest(q: string, signal: AbortSignal): Promise<readonly ContractTagSuggestion[]> {
  const response = await suggestContractTags({ q, limit: SUGGEST_LIMIT }, signal);
  return response.suggestions;
}

export function TagInput({
  id,
  label,
  value,
  onChange,
  disabled = false,
  max = TAGS_PER_CONTRACT_MAX,
  mode = 'edit',
  suggest = defaultSuggest,
  placeholder,
}: TagInputProps) {
  const listId = useId();
  const previewId = useId();
  const [raw, setRaw] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<readonly ContractTagSuggestion[]>([]);
  const [status, setStatus] = useState<SuggestStatus>({ kind: 'idle' });
  const [active, setActive] = useState(-1);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const preview = previewOf(raw);
  const full = value.length >= max;
  /* The prefix the server matches on: normalised, so `Fin` and `fin` are one request. */
  const prefix = preview.kind === 'ok' ? preview.tag : '';

  useEffect(() => {
    if (disabled || prefix === '') {
      abortRef.current?.abort();
      abortRef.current = null;
      setResults([]);
      setStatus({ kind: 'idle' });
      setActive(-1);
      return;
    }
    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus({ kind: 'searching' });
      void (async () => {
        try {
          const suggestions = await suggest(prefix, controller.signal);
          if (controller.signal.aborted) return;
          setResults(suggestions);
          setStatus({ kind: 'done' });
          setActive(-1);
        } catch (error) {
          if (controller.signal.aborted) return;
          if (error instanceof ApiError && error.isUnauthenticated) {
            void msalInstance.loginRedirect(loginRequest);
            return;
          }
          setStatus({
            kind: 'error',
            detail: error instanceof ApiError ? error.message : 'The tag suggestions did not reach the API.',
          });
        }
      })();
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [disabled, prefix, suggest]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /* The blur timer that lets a mousedown on an option land before the list
   * closes. Held in a ref and cleared on unmount so it can never fire after the
   * component is gone — in tests that surfaced as an unhandled
   * "window is not defined" once the DOM environment was torn down. */
  const blurTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (blurTimerRef.current !== null) window.clearTimeout(blurTimerRef.current);
    },
    [],
  );

  function commit(tag: string): void {
    if (disabled) return;
    if (!value.includes(tag)) {
      if (full) return;
      onChange([...value, tag]);
    }
    setRaw('');
    setOpen(false);
    setActive(-1);
  }

  function remove(tag: string): void {
    if (disabled) return;
    onChange(value.filter((held) => held !== tag));
    inputRef.current?.focus();
  }

  /* Rows: the suggestions not already held, then (edit mode) the create row when no exact match. */
  const shown = results.filter((suggestion) => !value.includes(suggestion.tag));
  const exact = preview.kind === 'ok' && (results.some((s) => s.tag === preview.tag) || value.includes(preview.tag));
  const createRow = mode === 'edit' && preview.kind === 'ok' && !exact ? preview.tag : null;
  const rowCount = shown.length + (createRow === null ? 0 : 1);
  const showList = open && !disabled && prefix !== '' && (rowCount > 0 || status.kind !== 'idle');
  const optionId = (index: number): string => `${listId}-opt-${String(index)}`;
  const activeId = active >= 0 && active < rowCount ? optionId(active) : undefined;

  function pickActive(): boolean {
    if (active < 0 || active >= rowCount) return false;
    const suggestion = shown[active];
    if (suggestion !== undefined) {
      commit(suggestion.tag);
      return true;
    }
    if (createRow !== null) {
      commit(createRow);
      return true;
    }
    return false;
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (rowCount === 0) return;
      setActive((current) => {
        if (event.key === 'ArrowDown') return current + 1 >= rowCount ? 0 : current + 1;
        return current - 1 < 0 ? rowCount - 1 : current - 1;
      });
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (event.key === 'Enter' || event.key === ',' || (event.key === 'Tab' && raw.trim() !== '')) {
      if (pickActive()) {
        event.preventDefault();
        return;
      }
      if (preview.kind === 'ok') {
        /* Filter mode commits only a tag the scope knows; a stranger stays as text. */
        if (mode === 'filter' && !exact) {
          if (event.key !== 'Tab') event.preventDefault();
          return;
        }
        event.preventDefault();
        commit(preview.tag);
        return;
      }
      if (event.key !== 'Tab') event.preventDefault();
      return;
    }
    if (event.key === 'Backspace' && raw === '' && value.length > 0) {
      const last = value[value.length - 1];
      if (last !== undefined) remove(last);
    }
  }

  const previewText =
    preview.kind === 'ok'
      ? `will be stored as “${preview.tag}”`
      : preview.kind === 'bad'
        ? preview.reason
        : full
          ? `at most ${String(max)} tags`
          : '';

  return (
    <div className={`ti${disabled ? ' ti-disabled' : ''}`} data-mode={mode}>
      <ul className="ti-chips" aria-label={label === undefined ? undefined : `${label} — current tags`}>
        {value.map((tag) => (
          <li key={tag} className="tag ti-chip">
            {tag}
            <button
              type="button"
              className="ti-x"
              disabled={disabled}
              aria-label={`Remove tag ${tag}`}
              onClick={() => remove(tag)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="ti-control">
        <input
          ref={inputRef}
          id={id}
          className="ti-input"
          type="text"
          role="combobox"
          autoComplete="off"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={activeId}
          aria-describedby={previewId}
          aria-invalid={preview.kind === 'bad' ? true : undefined}
          value={raw}
          disabled={disabled || (full && raw === '')}
          placeholder={placeholder ?? (mode === 'filter' ? 'Filter by tag…' : full ? 'No more tags' : 'Add a tag…')}
          onChange={(event) => {
            setRaw(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            /* Let a mousedown on an option land first. */
            if (blurTimerRef.current !== null) window.clearTimeout(blurTimerRef.current);
            blurTimerRef.current = window.setTimeout(() => {
              blurTimerRef.current = null;
              setOpen(false);
            }, 120);
          }}
          onKeyDown={onKeyDown}
        />
        {showList ? (
          <ul id={listId} className="ti-list" role="listbox" aria-label={label === undefined ? 'Tag suggestions' : `${label} suggestions`}>
            {shown.map((suggestion, index) => (
              <li
                key={suggestion.tag}
                id={optionId(index)}
                role="option"
                aria-selected={active === index}
                className={`ti-opt${active === index ? ' active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(suggestion.tag);
                }}
              >
                <span className="ti-opt-tag">{suggestion.tag}</span>
                <span className="mut ti-opt-n">
                  {suggestion.contracts.toLocaleString()} {suggestion.contracts === 1 ? 'contract' : 'contracts'}
                </span>
              </li>
            ))}
            {createRow !== null ? (
              <li
                id={optionId(shown.length)}
                role="option"
                aria-selected={active === shown.length}
                className={`ti-opt ti-create${active === shown.length ? ' active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(createRow);
                }}
              >
                Create “{createRow}”
              </li>
            ) : null}
            {status.kind === 'searching' && rowCount === 0 ? (
              <li className="ti-hint mut" role="presentation">
                Looking up tags in your scope…
              </li>
            ) : null}
            {status.kind === 'done' && rowCount === 0 ? (
              <li className="ti-hint mut" role="presentation">
                {mode === 'filter' ? 'No contract in your scope carries a tag starting like that.' : 'No matching tag yet.'}
              </li>
            ) : null}
            {status.kind === 'error' ? (
              <li className="ti-hint bad" role="presentation">
                {status.detail}
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
      <p id={previewId} className={`ti-preview mut${preview.kind === 'bad' ? ' bad' : ''}`} aria-live="polite">
        {previewText}
      </p>
    </div>
  );
}
